// React imports
import { useState, useEffect, useMemo, useRef } from 'react';

// Generic imports
import _ from 'lodash';
import validator from 'email-validator';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import Alert from '@mui/material/Alert';
import { useTheme, alpha } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faPencil } from '@fortawesome/pro-light-svg-icons';
import { faHatWizard } from '@fortawesome/free-solid-svg-icons';

// Local imports
import OpportunityProductsPane from './OpportunityProductsPane.js';
import OpportunityAccountPane from './OpportunityAccountPane.js';
import OpportunityRoutesPane from './OpportunityRoutesPane.js';
import OpportunityIncotermPane from './OpportunityIncotermPane.js';
import OpportunityDescriptionPane from './OpportunityDescriptionPane.js';
import { ProductDescription, ProductsSummary } from '../../utils/ProductHelperComponents.js';
import { checkProductErrors, createEmptyProduct, getProductName } from '../utils/product.js';
import EmailMagic from './OpportunityOneOffEmailBuilder.emailmagic.js';
import AiCorrectionNote from './AiCorrectionNote.js';
import readableId from '../../ReadableId.js';
import { StatusMenu } from './OpportunityPreviewAndSendBase.js';

dayjs.extend(advancedFormat);

function OpportunityOneOffAccountRouteProductsSelector(props) {
    const { setProducts, org, me, accounts, account, setAccount, routes, setRoutes,
        seaPorts, unlocode, incoterm, setIncoterm, accesstoken, transportationMethod,
        setTransportationMethod, products, source, status, setStatus, updates, setUpdates,
        emailLoginInfo, description, setDescription, locked, forceOpen, setForceOpen,
        opportunity, organization, onNewOpportunity,
        disabledTransportationMethods, disabledTransportationMethodsTooltip,
    } = props;

    const theme = useTheme();

    const [ showConfiguratePane, setShowConfiguratePane ] = useState(source?.type === 'email' && status === 'pre-draft' ? 'emailmagic' : null);
    const [ statusMessage, setStatusMessage ] = useState(null);

    const productErrors = useMemo(() => {
        if (!products) return [];
        return products.map(p => checkProductErrors({ product: p }));
    }, [products]);

    // Product ids that have costs linked to them (via any offer field's buyingRates).
    // Removing such a product unlinks its costs irreversibly, so ProductsEditor asks
    // the user to confirm before committing a removal.
    const linkedCostProductIds = useMemo(() => {
        const ids = new Set();
        (opportunity?.offers || []).forEach(o => (o.sections || []).forEach(s => (s.offers || []).forEach(so => (so.fields || []).forEach(f => (f.buyingRates || []).forEach(br => {
            if (br.opportunityProductId) ids.add(br.opportunityProductId);
        })))));
        return ids;
    }, [opportunity]);

    const _account = useMemo(() => {
        if (!account) return null;
        if (!account.id) return account;
        const a = accounts?.find(a => a.id === account.id);
        return {...(a || {}), ...(account || {})}
    }, [account, accounts]);

    useEffect(() => {
        if (forceOpen === 'account' || forceOpen === 'transportationMethod' || forceOpen === 'incoterm' || forceOpen === 'products' || forceOpen === 'description') {
            setShowConfiguratePane(forceOpen);
            setForceOpen(null);
        }
    }, [forceOpen]);

    return (
        <>
            <Box sx={{ background: 'transparent', padding: '6px 24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                        Shipment Details
                    </Typography>
                    {opportunity && (
                        <Typography variant='caption' component='div' sx={{ fontSize: '11px', color: theme.palette.text.secondary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Opportunity ID <b>{readableId(opportunity)}</b> · Created by <b>{opportunity.createdBy}</b> · Owner is <b>{opportunity.owner}</b> · Created on <b>{dayjs(opportunity.created).format('MMMM Do YYYY, h:mm A')}</b>
                            {organization && <> · <StatusMenu
                                opportunity={opportunity}
                                disabled={opportunity.status === 'deleted' || (me.email !== opportunity.owner && me.email !== 'admin@monada.ai')}
                                organization={organization}
                                accesstoken={accesstoken}
                                onNewOpportunity={onNewOpportunity}
                                setMessage={setStatusMessage}
                            /></>}
                        </Typography>
                    )}
                </Box>
                <Box>
                    {emailLoginInfo && (
                        <Tooltip title="Add details with AI">
                            <span>
                                <IconButton 
                                    onClick={() => setShowConfiguratePane('emailmagic')} 
                                    disabled={locked} 
                                    sx={{ 
                                        color: theme.palette.primary.main, 
                                        minWidth: '36px',
                                        width: '36px',
                                        height: '36px',
                                        padding: '0px',
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        borderRadius: '6px',
                                        '&:hover': { 
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            borderColor: theme.palette.primary.main,
                                            color: theme.palette.primary.main
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: theme.palette.action.disabled,
                                            color: theme.palette.action.disabled
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faHatWizard} style={{ fontSize: '14px' }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            </Box>
            <AiCorrectionNote
                updates={updates}
                transportationMethod={transportationMethod}
                incoterm={incoterm}
                routes={routes}
                products={products}
                opportunityId={opportunity?.id}
                org={org}
                accesstoken={accesstoken}
                locked={locked}
            />
            <Box sx={{ display: 'flex', gap: '12px', paddingBottom: '12px' }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <Typography variant='caption' sx={{ fontWeight: 800, fontSize: '11px', lineHeight: 1.5 }}>
                            Account<Box component='span' sx={{ color: 'error.main', ml: '2px' }}>*</Box>
                        </Typography>
                        <IconButton 
                            size='small' 
                            onClick={() => setShowConfiguratePane('account')} 
                            disabled={locked} 
                            sx={{ 
                                padding: '2px',
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: 'transparent'
                                },
                                '&.Mui-disabled': {
                                    color: theme.palette.action.disabled
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faPencil} style={{ fontSize: '14px' }} />
                        </IconButton>
                    </Box>
                    {_.isEmpty(account) && (
                        <Typography variant='caption' sx={{ fontStyle: 'italic', flex: 1, padding: '4px 0px', fontSize: '11px' }}>
                            Not Set
                        </Typography>
                    )}
                    {!_.isEmpty(account) && (
                        <Typography variant='caption' sx={{ fontStyle: 'italic', flex: 1, padding: '4px 0px', fontSize: '11px' }}>
                            {_account.name}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', borderLeft: `1px solid ${theme.palette.divider}`, borderRight: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <Typography variant='caption' sx={{ fontWeight: 800, fontSize: '11px', lineHeight: 1.5 }}>
                            Transportation, Routes, and Incoterm<Box component='span' sx={{ color: 'error.main', ml: '2px' }}>*</Box>
                        </Typography>
                        <IconButton 
                            size='small' 
                            onClick={() => setShowConfiguratePane('transportationMethod')} 
                            disabled={locked} 
                            sx={{ 
                                padding: '2px',
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: 'transparent'
                                },
                                '&.Mui-disabled': {
                                    color: theme.palette.action.disabled
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faPencil} style={{ fontSize: '14px' }} />
                        </IconButton>
                    </Box>
                    {!transportationMethod && (
                        <Typography variant='caption' sx={{ fontStyle: 'italic', flex: 1, padding: '4px 0px', fontSize: '11px' }}>
                            Not Set
                        </Typography>
                    )}
                    {!!transportationMethod && (
                        <Typography variant='caption' sx={{ fontStyle: 'italic', flex: 1, padding: '4px 0px', fontSize: '11px' }}>
                            Method: {_.startCase(transportationMethod)}
                            {incoterm
                                ? `, Incoterm: ${incoterm}`
                                : <Box component='span' sx={{ color: 'text.disabled' }}>, Incoterm is missing</Box>}
                            {(routes || []).map(r => <Box key={`${r.source?.id}-${r.destination?.id}`}>{r.source?.text || 'Any'} → {r.destination?.text || 'Any'}</Box>)}
                        </Typography>
                    )}
                </Box>
                {setProducts && (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '12px', borderRight: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <Typography variant='caption' sx={{ fontWeight: 800, fontSize: '11px', lineHeight: 1.5 }}>
                                Products<Box component='span' sx={{ color: 'error.main', ml: '2px' }}>*</Box>
                            </Typography>
                            <IconButton 
                                size='small' 
                                onClick={() => setShowConfiguratePane('products')} 
                                disabled={locked} 
                                sx={{ 
                                    padding: '2px',
                                    color: '#64748b',
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                        backgroundColor: 'transparent'
                                    },
                                    '&.Mui-disabled': {
                                        color: '#cbd5e1'
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faPencil} style={{ fontSize: '14px' }} />
                            </IconButton>
                        </Box>
                        {_.isEmpty(products) && (
                            <Typography variant='caption' sx={{ fontStyle: 'italic', flex: 1, padding: '4px 0px', fontSize: '11px' }}>
                                Not Set
                            </Typography>
                        )}
                        {!_.isEmpty(products) && (
                            <Typography variant='caption' sx={{ fontStyle: 'italic', flex: 1, padding: '4px 0px', fontSize: '11px' }}>
                                {products.map((product, index) =>
                                    <Box key={product.id}>
                                        <ProductDescription product={product} transportationMethod={transportationMethod} allProducts={products} organization={org} />
                                        {!_.isEmpty(productErrors[index]) && (
                                            <Tooltip title={`Error in the following fields: ${productErrors[index].map(e => _.startCase(e)).join(', ')}.`}>
                                                <Typography variant='caption' sx={{ color: 'red', fontSize: '11px' }}>
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                                <ProductsSummary products={products} transportationMethod={transportationMethod} organization={org} />
                            </Typography>
                        )}
                    </Box>
                )}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', maxWidth: '200px', gap: '4px', paddingRight: '12px', textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <Typography variant='caption' sx={{ fontWeight: 800, fontSize: '11px', lineHeight: 1.5 }}>
                            Description
                        </Typography>
                        <IconButton 
                            size='small' 
                            onClick={() => setShowConfiguratePane('description')} 
                            disabled={locked} 
                            sx={{ 
                                padding: '2px',
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: 'transparent'
                                },
                                '&.Mui-disabled': {
                                    color: theme.palette.action.disabled
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faPencil} style={{ fontSize: '14px' }} />
                        </IconButton>
                    </Box>
                    {_.isEmpty(description?.freeText) && (
                        <Typography variant='caption' sx={{ fontStyle: 'italic', flex: 1, padding: '4px 0px', fontSize: '11px' }}>
                            Optional description shown in the Opportunities page
                        </Typography>
                    )}
                    {!_.isEmpty(description?.freeText) && (
                        <Typography variant='caption' sx={{ fontStyle: 'italic', flex: 1, padding: '4px 0px', fontSize: '11px' }}>
                            {description?.freeText}
                        </Typography>
                    )}
                </Box>
            </Box>

            {showConfiguratePane === 'account' && (
                <AccountEditor
                    account={_account}
                    setAccount={setAccount}
                    accounts={accounts}
                    onClose={() => setShowConfiguratePane(null)}
                />
            )}

            {showConfiguratePane === 'transportationMethod' && (
                <TransportationMethodEditor
                    transportationMethod={transportationMethod}
                    setTransportationMethod={setTransportationMethod}
                    routes={routes}
                    setRoutes={setRoutes}
                    incoterm={incoterm}
                    setIncoterm={setIncoterm}
                    seaPorts={seaPorts}
                    unlocode={unlocode}
                    org={org}
                    accesstoken={accesstoken}
                    onClose={() => setShowConfiguratePane(null)}
                    disabledTransportationMethods={disabledTransportationMethods}
                    disabledTransportationMethodsTooltip={disabledTransportationMethodsTooltip}
                />
            )}

            {showConfiguratePane === 'products' && (
                <ProductsEditor
                    products={products}
                    setProducts={setProducts}
                    transportationMethod={transportationMethod}
                    onClose={() => setShowConfiguratePane(null)}
                    org={org}
                    accesstoken={accesstoken}
                    linkedCostProductIds={linkedCostProductIds}
                />
            )}

            {showConfiguratePane === 'description' && (
                <DescriptionEditor
                    description={description}
                    setDescription={setDescription}
                    onClose={() => setShowConfiguratePane(null)}
                />
            )}

            {showConfiguratePane === 'emailmagic' && (
                <EmailMagic
                    accesstoken={accesstoken}
                    org={org}
                    onClose={() => {
                        if (setStatus && status === 'pre-draft') setStatus('draft');
                        setShowConfiguratePane(null)
                    }}
                    setTransportationMethod={setTransportationMethod}
                    setRoutes={setRoutes}
                    setIncoterm={setIncoterm}
                    products={products}
                    setProducts={setProducts}
                    seaPorts={seaPorts}
                    unlocode={unlocode}
                    updates={updates}
                    setUpdates={setUpdates}
                    me={me}
                    source={source}
                    emailLoginInfo={emailLoginInfo}
                    accounts={accounts}
                    setAccount={setAccount}
                />
            )}
        </>
    );
}

export default OpportunityOneOffAccountRouteProductsSelector;

function AccountEditor(props) {
    const { account, setAccount, accounts, onClose } = props;

    const [innerAccount, setInnerAccount] = useState(account);
    const [ errors, setErrors ] = useState([]);
    const accountIdRef = useRef(account?.id);
    const isInitialMount = useRef(true);

    useEffect(() => {
        // Only update innerAccount on initial mount or if account ID actually changed
        // This prevents resetting the input when user is typing
        if (isInitialMount.current) {
            isInitialMount.current = false;
            accountIdRef.current = account?.id;
            return;
        }
        
        // Only update if the account ID changed (meaning a different account was selected externally)
        if (accountIdRef.current !== account?.id) {
            accountIdRef.current = account?.id;
            setInnerAccount(account);
        }
    }, [ account?.id ]);

    return (
        <Dialog open={true} onClose={onClose} fullWidth maxWidth='sm' PaperProps={{ sx: { overflowY: 'visible' } }}>
            <DialogTitle sx={{ paddingBottom: '8px' }}>
                <Typography
                    variant='h5'
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '18px',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        color: '#0f172a',
                        marginBottom: '4px'
                    }}
                >
                    Set Account
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ overflowY: 'visible'}}>
                <OpportunityAccountPane
                    account={innerAccount}
                    setAccount={setInnerAccount}
                    accounts={accounts}
                    width='100%'
                    errors={errors}
                    setErrors={setErrors}
                />
            </DialogContent>
            <DialogActions sx={{ padding: '24px', gap: '12px' }}>
                <Button 
                    onClick={onClose}
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '9999px',
                        padding: '10px 20px',
                        border: '1px solid transparent',
                        color: '#334155',
                        backgroundColor: 'transparent',
                        transition: 'all 200ms ease-in-out',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            border: '1px solid #cbd5e1',
                            color: '#0f172a'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    disabled={errors.length > 0}
                    onClick={() => {
                        setAccount(innerAccount);
                        onClose();
                    }}
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '9999px',
                        padding: '10px 24px',
                        backgroundColor: '#4f46e5',
                        color: '#ffffff',
                        boxShadow: 'none',
                        transition: 'all 200ms ease-in-out',
                        '&:hover': {
                            backgroundColor: '#4338ca',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            transform: 'translateY(-1px)'
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#cbd5e1',
                            color: '#ffffff'
                        }
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function TransportationMethodEditor(props) {
    const { transportationMethod, setTransportationMethod, routes, setRoutes, incoterm, setIncoterm, seaPorts, unlocode, org, accesstoken, onClose, disabledTransportationMethods, disabledTransportationMethodsTooltip } = props;
    const [ innerTransportationMethod, setInnerTransportationMethod ] = useState(transportationMethod);
    const [ innerRoutes, setInnerRoutes ] = useState(routes);
    const [ innerIncoterm, setInnerIncoterm ] = useState(incoterm);

    return (
        <Dialog open={true} onClose={onClose} fullWidth maxWidth='sm' PaperProps={{ sx: { overflowY: 'visible' } }}>
            <DialogTitle sx={{ paddingBottom: '8px' }}>
                <Typography
                    variant='h5'
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '18px',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        color: '#0f172a',
                        marginBottom: '4px'
                    }}
                >
                    Set Routes & Incoterm
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ overflowY: 'visible'}}>
                <OpportunityRoutesPane
                    mode='one-off'
                    transportationMethod={innerTransportationMethod}
                    setTransportationMethod={setInnerTransportationMethod}
                    routes={innerRoutes}
                    setRoutes={setInnerRoutes}
                    seaPorts={seaPorts}
                    unlocode={unlocode}
                    organization={org}
                    accesstoken={accesstoken}
                    disabledTransportationMethods={disabledTransportationMethods}
                    disabledTransportationMethodsTooltip={disabledTransportationMethodsTooltip}
                />
                {innerTransportationMethod && (
                    <OpportunityIncotermPane
                        incoterm={innerIncoterm}
                        setIncoterm={setInnerIncoterm}
                        transportationMethod={innerTransportationMethod}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ padding: '24px', gap: '12px' }}>
                <Button 
                    onClick={onClose}
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '9999px',
                        padding: '10px 20px',
                        border: '1px solid transparent',
                        color: '#334155',
                        backgroundColor: 'transparent',
                        transition: 'all 200ms ease-in-out',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            border: '1px solid #cbd5e1',
                            color: '#0f172a'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={() => {
                        setTransportationMethod(innerTransportationMethod);
                        setRoutes(innerRoutes);
                        setIncoterm(innerIncoterm);
                        onClose();
                    }}
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '9999px',
                        padding: '10px 24px',
                        backgroundColor: '#4f46e5',
                        color: '#ffffff',
                        boxShadow: 'none',
                        transition: 'all 200ms ease-in-out',
                        '&:hover': {
                            backgroundColor: '#4338ca',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ProductsEditor(props) {
    const { products, setProducts, org, accesstoken, transportationMethod, onClose, linkedCostProductIds = new Set() } = props;
    const [ innerProducts, setInnerProducts ] = useState(() => {
        if (_.isEmpty(products)) {
            if (transportationMethod === 'sea') return [];
            return [createEmptyProduct({ transportationMethod })];
        }
        return products;
    });
    const [ errors, setErrors ] = useState([]);
    // Products that have linked costs and are being removed OR type-changed by this edit —
    // set when the user tries to save so we can confirm before the used costs are unlinked.
    // Each entry: { id, name, action: 'removed' | 'changed' }.
    const [ affectedWithCosts, setAffectedWithCosts ] = useState(null);

    function commit() {
        setProducts(innerProducts);
        onClose();
    }

    function onSave() {
        let er = [];
        innerProducts.forEach((product, i) => {
            const e = checkProductErrors({ product });
            er = [...er, ...e.map(p => `product:${i}:${p}`)];
        });
        if (!_.isEmpty(er)) {
            setErrors(er);
            return;
        }
        // Warn before removing OR type-changing a product that has costs linked to it — both
        // unlink the used costs (removal is permanent; a type change moves them to alternative).
        const affected = [];
        (products || []).forEach(orig => {
            if (!linkedCostProductIds.has(orig.id)) return;
            const now = innerProducts.find(p => p.id === orig.id);
            if (!now) affected.push({ id: orig.id, name: getProductName({ product: orig }), action: 'removed' });
            else if (now.type !== orig.type) affected.push({ id: orig.id, name: `${getProductName({ product: orig })} → ${now.type}`, action: 'changed' });
        });
        if (affected.length > 0) {
            setAffectedWithCosts(affected);
            return;
        }
        commit();
    }

    if (!transportationMethod) {
        return (
            <Dialog open={true} onClose={onClose} fullWidth maxWidth='sm' PaperProps={{ sx: { overflowY: 'visible' } }}>
                <DialogTitle sx={{ paddingBottom: '8px' }}>
                    <Typography
                        variant='h5'
                        sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: '18px',
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            color: '#0f172a',
                            marginBottom: '4px'
                        }}
                    >
                        Set Products
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography>Please select a transportation method first</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={true} onClose={onClose} fullWidth maxWidth='lg' PaperProps={{ sx: { overflowY: 'visible' } }}>
            <DialogTitle sx={{ paddingBottom: '8px' }}>
                <Typography
                    variant='h5'
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '18px',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        color: '#0f172a',
                        marginBottom: '4px'
                    }}
                >
                    Set Products
                </Typography>
            </DialogTitle>
            <DialogContent>
                <OpportunityProductsPane
                    products={innerProducts}
                    setProducts={setInnerProducts}
                    organization={org}
                    accesstoken={accesstoken}
                    transportationMethod={transportationMethod}
                    errors={errors}
                    setErrors={setErrors}
                />
            </DialogContent>
            <DialogActions sx={{ padding: '24px', gap: '12px' }}>
                <Button 
                    onClick={onClose}
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '9999px',
                        padding: '10px 20px',
                        border: '1px solid transparent',
                        color: '#334155',
                        backgroundColor: 'transparent',
                        transition: 'all 200ms ease-in-out',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            border: '1px solid #cbd5e1',
                            color: '#0f172a'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={onSave}
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '9999px',
                        padding: '10px 24px',
                        backgroundColor: '#4f46e5',
                        color: '#ffffff',
                        boxShadow: 'none',
                        transition: 'all 200ms ease-in-out',
                        '&:hover': {
                            backgroundColor: '#4338ca',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    Save
                </Button>
            </DialogActions>
            <Dialog open={!!affectedWithCosts} onClose={() => setAffectedWithCosts(null)} maxWidth='xs' fullWidth sx={{ zIndex: 10000001 }}>
                <DialogTitle>Change products with linked costs?</DialogTitle>
                <DialogContent>
                    <Alert severity='warning' sx={{ mb: 1 }}>
                        {(affectedWithCosts || []).some(a => a.action === 'removed')
                            ? 'Removing a product permanently unlinks its costs.'
                            : "Changing a product's type unlinks its costs (they move to Alternative Costs)."}
                    </Alert>
                    <DialogContentText component='div'>
                        The following {(affectedWithCosts || []).length > 1 ? 'products have' : 'product has'} costs currently used in this quote:
                        <ul style={{ marginTop: 8, marginBottom: 0 }}>
                            {(affectedWithCosts || []).map(a => (
                                <li key={a.id}>{a.name}{a.action === 'removed' ? ' — removing' : ' — changing type'}</li>
                            ))}
                        </ul>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAffectedWithCosts(null)}>Cancel</Button>
                    <Button color='error' variant='contained' onClick={() => { setAffectedWithCosts(null); commit(); }}>
                        Continue &amp; unlink costs
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}

function DescriptionEditor(props) {
    const { description, setDescription, onClose } = props;
    const [ innerDescription, setInnerDescription ] = useState(description);

    function onSave() {
        setDescription(innerDescription);
        onClose();
    }

    useEffect(() => {
        setInnerDescription(description);
    }, [ description ]);

    return (
        <Dialog open={true} onClose={onClose} fullWidth maxWidth='lg' PaperProps={{ sx: { overflowY: 'visible' } }}>
            <DialogTitle sx={{ paddingBottom: '8px' }}>
                <Typography
                    variant='h5'
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '18px',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        color: '#0f172a',
                        marginBottom: '4px'
                    }}
                >
                    Set Description
                </Typography>
            </DialogTitle>
            <DialogContent>
                <OpportunityDescriptionPane
                    description={innerDescription}
                    setDescription={setInnerDescription}
                />
            </DialogContent>
            <DialogActions sx={{ padding: '24px', gap: '12px' }}>
                <Button 
                    onClick={onClose}
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '9999px',
                        padding: '10px 20px',
                        border: '1px solid transparent',
                        color: '#334155',
                        backgroundColor: 'transparent',
                        transition: 'all 200ms ease-in-out',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            border: '1px solid #cbd5e1',
                            color: '#0f172a'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={onSave}
                    sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '9999px',
                        padding: '10px 24px',
                        backgroundColor: '#4f46e5',
                        color: '#ffffff',
                        boxShadow: 'none',
                        transition: 'all 200ms ease-in-out',
                        '&:hover': {
                            backgroundColor: '#4338ca',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}